import * as B from '@blueprintjs/core';
import moment from 'moment';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {getRunTitleFromKey} from 'app/components/AgentExplorer/PromptView/promptConfigs';
import SparkleIcon from 'app/components/Library/Icons/SparkleIcon';
import {
  ApiRecentRun,
  PendingBulkRun,
  PendingRun,
  pendingRunUrl,
  usePendingAgentRuns,
} from 'app/modules/AgentRuns/usePendingAgentRuns';

import cs from './styles.module.scss';

function usePendingEntries() {
  const {pendingRuns, pendingBulkRuns, recentRuns} = usePendingAgentRuns();
  const runsById = React.useMemo(
    () => new Map((recentRuns ?? []).map((run) => [run.id, run])),
    [recentRuns]
  );
  // A batch's properties are listed as its one row, not as runs of their own — but only while that
  // row is there to stand in for them.
  const isBatched = (entry: PendingRun) => {
    const workflowRunId = runsById.get(entry.runId)?.agentWorkflowRunId;
    return workflowRunId != null && pendingBulkRuns.has(workflowRunId);
  };

  return {
    standaloneRuns: [...pendingRuns.values()].filter((entry) => !isBatched(entry)),
    bulkRuns: [...pendingBulkRuns.values()],
    runsById,
    recentRuns: recentRuns ?? [],
  };
}

function bulkRunDetails(recentRuns: ApiRecentRun[], entry: PendingBulkRun) {
  const runs = recentRuns.filter((run) => run.agentWorkflowRunId === entry.workflowRunId);

  return {
    title: runs[0]?.agentWorkflowTitle ?? entry.title,
    // Absent until a job creates the batch's runs; they sort newest-first, so they're on this page.
    startedAt: runs.map((run) => run.startedAt).sort()[0],
  };
}

const startedText = (startedAt: string | undefined) =>
  startedAt ? `Started ${moment(startedAt).fromNow()}` : undefined;

const RunCard: React.FC<{title: string; meta: (string | undefined)[]; url?: string}> = ({
  title,
  meta,
  url,
}) => {
  const navigate = useNavigate();

  return (
    <B.Card className={cs.runCard} compact>
      <div className={cs.runInfo}>
        <div className={cs.runTitle}>{title}</div>
        <div className={cs.runMeta}>{meta.filter(Boolean).join(' · ')}</div>
      </div>
      <B.Tag minimal round icon={<B.Spinner size={12} />} className={cs.statusTag}>
        Running
      </B.Tag>
      {url && (
        <B.Button minimal small icon="arrow-right" title="View" onClick={() => navigate(url)} />
      )}
    </B.Card>
  );
};

const StandaloneRunCard: React.FC<{entry: PendingRun; run: ApiRecentRun | undefined}> = ({
  entry,
  run,
}) => (
  <RunCard
    title={
      run ? getRunTitleFromKey(run.promptKey, run.sceneCount === 2, run.samPrompt) : 'Agent run'
    }
    meta={[run?.featureName, startedText(run?.startedAt)]}
    url={pendingRunUrl(entry, run)}
  />
);

const BulkRunCard: React.FC<{entry: PendingBulkRun; recentRuns: ApiRecentRun[]}> = ({
  entry,
  recentRuns,
}) => {
  const {title, startedAt} = bulkRunDetails(recentRuns, entry);

  return (
    <RunCard
      title={title ?? 'Run plan'}
      // Per-property progress lives on the plan run page, which the row links to.
      meta={[startedText(startedAt) ?? 'Starting']}
      url={entry.url}
    />
  );
};

const PendingRunsCards: React.FC = () => {
  const {standaloneRuns, bulkRuns, runsById, recentRuns} = usePendingEntries();

  return (
    <div className={cs.popoverBody}>
      <div className={cs.popoverTitle}>Agent runs in progress</div>
      {bulkRuns.map((entry) => (
        <BulkRunCard key={`plan-${entry.workflowRunId}`} entry={entry} recentRuns={recentRuns} />
      ))}
      {standaloneRuns.map((entry) => (
        <StandaloneRunCard key={entry.runId} entry={entry} run={runsById.get(entry.runId)} />
      ))}
    </div>
  );
};

/**
 * AppNav pill shown while the current user has agent runs in progress;
 * clicking opens status cards deep-linking into each run.
 */
const PendingRunsIndicator: React.FC = () => {
  const {standaloneRuns, bulkRuns} = usePendingEntries();

  if (standaloneRuns.length === 0 && bulkRuns.length === 0) {
    return null;
  }

  const label = [
    standaloneRuns.length &&
      `${standaloneRuns.length} Agent ${standaloneRuns.length === 1 ? 'run' : 'runs'}`,
    bulkRuns.length && `${bulkRuns.length} Run ${bulkRuns.length === 1 ? 'plan' : 'plans'}`,
  ]
    .filter(Boolean)
    .join(' and ');

  return (
    <B.Popover
      className={cs.pillPopoverTarget}
      content={<PendingRunsCards />}
      interactionKind="click"
      position={B.Position.BOTTOM_LEFT}
      minimal
    >
      <button className={cs.pill}>
        <SparkleIcon size={12} className={cs.sparkle} />
        {label} in progress
      </button>
    </B.Popover>
  );
};

export default PendingRunsIndicator;
